import { getStreamStatus, StreamStatusInfo } from '@/helpers/statusHelper'
import { Cast } from '@/modules/casts/classes'
import { SystemStatus } from '@/types/ops'
import { Stream, StreamStatusCode, StreamType } from '@/types/streams'
import { computed, ComputedRef } from '@vue/composition-api'

/* eslint-disable complexity */
const getOutputErrorMessage = (statusCode: StreamStatusCode, streamInfo: StreamStatusInfo|null,
                              isNimbleSrt: boolean) => {
  if (statusCode === StreamStatusCode.OutputLowBandwidth) {
    return 'Trouble sending output stream to target. Not enough upload bandwidth.'
  } else if (statusCode === StreamStatusCode.StreamBooting) {
    return 'Output is starting ...'
  } else if (statusCode === StreamStatusCode.StreamError || streamInfo?.active === false) {
    return 'Not streaming'
  } else if (statusCode === StreamStatusCode.None) {
    return 'Not streaming'
  } else if (streamInfo?.srt_state === SystemStatus.ERROR && !isNimbleSrt) {
    const msg = streamInfo?.srt_msg
    if (msg !== undefined && msg !== '') return `SRT: ${msg}`
    return 'SRT issues ...'
  }
  return ''
}
/* eslint-enable complexity */

const getInputErrorMessage = (statusCode: StreamStatusCode, streamInfo: StreamStatusInfo|null,
                              isNimbleSrt: boolean) => {
  if (statusCode === StreamStatusCode.SRTError && !isNimbleSrt) {
    const msg = streamInfo?.srt_msg
    if (msg !== undefined && msg !== '') return `SRT: ${msg}`
    return 'SRT issues ...'
  } else if (statusCode === StreamStatusCode.StreamNotConnected) {
    return 'Stream does not exist'
  } else if (statusCode === StreamStatusCode.StreamBooting) {
    return 'Ingest is starting ...'
  } else if (statusCode === StreamStatusCode.StreamError) {
    return 'Ingest has issues'
  }
  return ''
}

const useStreamStatus = (cast: ComputedRef<Cast|null>, streamId: ComputedRef<string|null>,
                         stream: ComputedRef<Stream|null>, refreshTick?: ComputedRef<number>) => {
  const startTime = computed(() => {
    if (!streamId.value) return 0
    const sourceStreamStartTime = cast.value?.source_streams[streamId.value]?.start_time ?? 0
    const customRtmpStartTime = cast.value?.custom_rtmp_destinations?.[streamId.value]?.start_time ?? 0
    const castStartTime = cast.value?.video_start_time ?? 0
    return Math.max(sourceStreamStartTime, customRtmpStartTime, castStartTime)
  })

  const isNimbleSrt = computed(() => {
    if (stream.value?.nimble_stream_url) return true
    return false
  })

  const maybeInputOrCasterStreamInfo: ComputedRef<StreamStatusInfo|null> = computed(() => {
    if (streamId.value === null) return null
    const sourceStream = cast.value?.source_streams[streamId.value]
    if (!sourceStream?.type) return null
    const streamInfo: StreamStatusInfo = {
      type: sourceStream.type,
      ngcvp_status: sourceStream.ngcvp_status,
      active: sourceStream.active,
      srt_state: sourceStream.srt_state,
      srt_msg: stream.value?.srt_msg
    }
    return streamInfo
  })

  const maybeOutputStreamInfo: ComputedRef<StreamStatusInfo|null> = computed(() => {
    if (streamId.value === null) return null
    const customRtmpInfo = cast.value?.custom_rtmp_destinations?.[streamId.value]
    const type = stream.value?.type || customRtmpInfo?.type
    if (!type) return null
    const streamInfo: StreamStatusInfo = {
      type,
      ngcvp_status: cast.value?.output_streams[streamId.value]?.ngcvp_status,
      active: customRtmpInfo?.active,
      srt_state: stream.value?.srt_state,
      srt_msg: stream.value?.srt_msg
    }
    return streamInfo
  })

  const streamInfo = computed(() => maybeInputOrCasterStreamInfo.value || maybeOutputStreamInfo.value)

  const isOutput = computed(() => {
    return streamId.value !== null && cast.value?.custom_rtmp_destinations?.[streamId.value] !== undefined
  })

  // eslint-disable-next-line complexity
  const statusCode = computed(() => {
    if (stream.value) {
      if (stream.value.type === StreamType.Broadcast && Object.keys(stream.value?.variants ?? {}).length === 0) {
        if (stream.value.srt_state === SystemStatus.ERROR) return StreamStatusCode.SRTError
        return StreamStatusCode.StreamNotConnected
      }
      if (stream.value.type === StreamType.Output && !stream.value.active) return StreamStatusCode.None
      // Is now covered by getStreamStatus (after 60s though)
      // if (isOutput.value && stream.value.srt_state === SystemStatus.ERROR) return StreamStatusCode.SRTError
    }
    if (streamInfo.value) {
      // We use an optional, dummy `refreshTick` Ref to be able to force a Vue reactivity update. Otherwise a bad stream
      // (that doesn't change anymore) won't switch to the error state after the "grace period" in getStreamStatus().
      const delta = Date.now() / 1000 - startTime.value + (refreshTick?.value ?? 0) * 0
      return getStreamStatus(streamInfo.value, undefined, false, delta)
    }
    return StreamStatusCode.StreamNotConnected
  })

  const errorMessage = computed(() => {
    if (isOutput.value) return getOutputErrorMessage(statusCode.value, streamInfo.value, isNimbleSrt.value)
    return getInputErrorMessage(statusCode.value, streamInfo.value, isNimbleSrt.value)
  })

  return {
    errorMessage,
    statusCode
  }
}

export default useStreamStatus
